import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

const RECENT_CASES_KEY = 'variant-lens.recentCases'
const RECENT_CASES_MAX = 8

function loadRecentCases(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENT_CASES_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.filter((v): v is string => typeof v === 'string').slice(0, RECENT_CASES_MAX)
  } catch {
    return []
  }
}

function recordRecentCase(caseId: string): void {
  try {
    const prev = loadRecentCases().filter((id) => id !== caseId)
    const next = [caseId, ...prev].slice(0, RECENT_CASES_MAX)
    window.localStorage.setItem(RECENT_CASES_KEY, JSON.stringify(next))
  } catch {
    // localStorage may be unavailable (private mode, SSR); silently ignore.
  }
}

// Curated case ids are upper-case by convention, so we upper-case a typed id
// for a forgiving match (csc9a7 -> CSC9A7). Benchmark sample-dir ids, however,
// end in a case-sensitive lower-case hash (e.g. HG002-cz1vkn5synzkxe,
// GEN-5496) that S3 will not match once folded. We detect that specific shape,
// a trailing "-{hash}" of at least 8 chars carrying both a lower-case letter
// and a digit, and preserve the original case only then. Every existing
// curated id (all letters/digits, or short hyphen suffixes like -P) is folded
// exactly as before. The input itself must NOT force-fold (no autoCapitalize
// or CSS uppercase): otherwise a mobile keyboard uppercases the hash before it
// reaches this logic, the regex stops matching, and the id is wrongly folded.
const BENCHMARK_DIR_RE = /-(?=[a-z0-9]*[a-z])(?=[a-z0-9]*[0-9])[a-z0-9]{8,}$/

// Upper bound on an order/analysis token. Mirrors the API's _MAX_TOKEN_LENGTH
// (128) in app/cases/order_lookup.py, so we never forward an unbounded string
// to the /orders/{token} resolver. Case ids keep their stricter 32-char rule.
const MAX_ORDER_TOKEN_LENGTH = 128

// A pasted token is "order-ish" (routes to /orders/{token} for MCDB resolution)
// when it clearly matches one of the shapes the order resolver accepts, and is
// otherwise treated as a sample/case/benchmark id (GEN-5657). We prefer a case
// id on genuine ambiguity, so each pattern is deliberately specific:
//   1. A canonical 8-4-4-4-12 UUID (an order or SeqOne analysis UUID).
//   2. A SeqOne analysis display name: OR..._IN..._{singleton|proband|duo|trio}_{n}[_ENV].
//   3. An id-gen order HRID: OR-#######[-ENV] or the dash-stripped OR#######.
// A bare sample id like CSC9A7 or CASE10-P matches none of these and stays a case.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const ANALYSIS_NAME_RE =
  /^[A-Z]{2}-?\d+(?:-[A-Z0-9]+)?_[A-Z]{2}-?\d+(?:-[A-Z0-9]+)?_(?:singleton|proband|duo|trio)_\d+(?:_[A-Z0-9]+)?$/i
const ORDER_HRID_RE = /^OR-?\d+(?:-[A-Za-z0-9]+)?$/i

function isOrderToken(raw: string): boolean {
  const trimmed = raw.trim()
  if (trimmed.length === 0 || trimmed.length > MAX_ORDER_TOKEN_LENGTH) return false
  return UUID_RE.test(trimmed) || ANALYSIS_NAME_RE.test(trimmed) || ORDER_HRID_RE.test(trimmed)
}

function normalizeCaseId(raw: string): string {
  const trimmed = raw.trim()
  return BENCHMARK_DIR_RE.test(trimmed) ? trimmed : trimmed.toUpperCase()
}

function isValidCaseId(value: string): boolean {
  return /^[A-Za-z0-9][A-Za-z0-9_-]{1,31}$/.test(value)
}

// The route to open a typed token: order-ish tokens go to the full order case
// view (the backend canonicalizes the token, so it is passed through untouched,
// never folded); everything else is a sample/case/benchmark id.
function destinationFor(raw: string): string | null {
  const trimmed = raw.trim()
  if (isOrderToken(trimmed)) {
    return `/orders/${encodeURIComponent(trimmed)}`
  }
  const normalized = normalizeCaseId(trimmed)
  return isValidCaseId(normalized) ? `/cases/${encodeURIComponent(normalized)}` : null
}

// The route to surface an analysis's member samples as a pick list (GEN-5674).
// Available only for order/analysis-shaped tokens; a bare case id has no analysis
// to resolve. The token is passed through untouched (case-significant, backend-
// canonicalized), like the order route.
function analysisSamplesDestinationFor(raw: string): string | null {
  const trimmed = raw.trim()
  return isOrderToken(trimmed) ? `/analyses/${encodeURIComponent(trimmed)}` : null
}

const Home: React.FC = () => {
  const navigate = useNavigate()
  const [caseId, setCaseId] = useState('')
  const [touched, setTouched] = useState(false)
  const [recent, setRecent] = useState<string[]>([])

  useEffect(() => {
    setRecent(loadRecentCases())
  }, [])

  const normalized = normalizeCaseId(caseId)
  const orderToken = isOrderToken(caseId)
  const destination = destinationFor(caseId)
  const analysisDestination = analysisSamplesDestinationFor(caseId)
  // An order-ish token is validated by shape (isOrderToken); a case id keeps the
  // stricter grammar. Either way, showError means "cannot route this input".
  const showError = touched && destination === null
  const isEmpty = caseId.trim().length === 0

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    setTouched(true)
    if (destination === null) return
    // Record the token exactly as it will be routed: the order token untouched
    // (case-significant, backend-canonicalized) or the normalized case id.
    recordRecentCase(orderToken ? caseId.trim() : normalized)
    navigate(destination)
  }

  // Surface an analysis's member samples as a pick list (GEN-5674). Only enabled
  // for order/analysis-shaped tokens; a bare case id has no analysis to resolve.
  const handleLookupSamples = () => {
    setTouched(true)
    if (analysisDestination === null) return
    recordRecentCase(caseId.trim())
    navigate(analysisDestination)
  }

  const handleRecentClick = (id: string) => {
    recordRecentCase(id)
    const dest = destinationFor(id)
    // A recent chip is always a token we routed before, but guard anyway so a
    // stale/hand-edited entry cannot navigate to a malformed path.
    if (dest !== null) navigate(dest)
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col items-center justify-center gap-8 px-4 py-16">
      <header className="flex flex-col items-center gap-3 text-center">
        <h1 className="text-text-heading text-3xl font-semibold tracking-tight sm:text-4xl">Variant Lens</h1>
        <p className="text-text-subtle max-w-lg text-base sm:text-lg">
          Pipeline-tag verdicts, hybrid short-read / long-read evidence, and the family IGV view for a single patient
          case. Complements SeqOne.
        </p>
      </header>

      <section
        aria-label="Open a case"
        className="border-border-subtle w-full rounded-2xl border bg-white p-6 shadow-sm sm:p-8"
      >
        <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          <label htmlFor="case-id-input" className="flex flex-col gap-1">
            <span className="text-text-heading text-sm font-medium">Open a case</span>
            <span className="text-text-subtle text-xs">
              Enter a sample or case ID (e.g. <code className="font-mono">CASE10-P</code> or{' '}
              <code className="font-mono">CSC9A7</code>), or an order / SeqOne analysis ID (e.g.{' '}
              <code className="font-mono">OR-67506308-DEV</code>). For a trio, enter the proband ID and its parents load
              alongside.
            </span>
          </label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              id="case-id-input"
              data-testid="home-case-id-input"
              type="text"
              inputMode="text"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="CSC9A7"
              value={caseId}
              onChange={(event) => {
                setCaseId(event.target.value)
                if (touched) setTouched(false)
              }}
              onBlur={() => setTouched(true)}
              aria-invalid={showError ? 'true' : 'false'}
              aria-describedby={showError ? 'home-case-id-error' : undefined}
              className={`flex-1 rounded-lg border bg-white px-4 py-3 font-mono text-base tracking-wide transition outline-none ${
                showError
                  ? 'border-error-500 focus:border-error-600 focus:ring-error-200 focus:ring-2'
                  : 'focus:border-brand-600 focus:ring-brand-200 border-gray-300 focus:ring-2'
              }`}
            />
            <button
              type="submit"
              data-testid="home-case-id-submit"
              disabled={isEmpty}
              className="bg-brand-600 hover:bg-brand-700 focus:ring-brand-300 rounded-lg px-6 py-3 text-base font-medium text-white shadow-sm transition focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
            >
              Open case
            </button>
            <button
              type="button"
              data-testid="home-analysis-samples-submit"
              onClick={handleLookupSamples}
              disabled={analysisDestination === null}
              title={
                analysisDestination === null ? 'Enter an order or SeqOne analysis ID to list its samples' : undefined
              }
              className="border-brand-200 text-brand-700 hover:border-brand-300 hover:bg-brand-50 focus:ring-brand-300 rounded-lg border px-6 py-3 text-base font-medium transition focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400"
            >
              Look up analysis samples
            </button>
          </div>
          {showError && (
            <p id="home-case-id-error" data-testid="home-case-id-error" role="alert" className="text-error-600 text-sm">
              Enter a sample/case ID (letters, digits, hyphens, underscores; 2-32 chars) or an order / analysis ID.
              Example: CSC9A7 or OR-67506308-DEV.
            </p>
          )}
        </form>

        {recent.length > 0 && (
          <div className="mt-6 border-t border-gray-100 pt-4">
            <p className="text-text-subtle text-xs font-medium tracking-wide uppercase">Recent cases</p>
            <ul className="mt-2 flex flex-wrap gap-2" data-testid="home-recent-cases">
              {recent.map((id) => (
                <li key={id}>
                  <button
                    type="button"
                    onClick={() => handleRecentClick(id)}
                    className="text-text-body hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 font-mono text-sm transition"
                  >
                    {id}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  )
}

export default Home
